/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useMemo, useCallback, useState } from 'react';
import { useRecoilState } from 'recoil';
import {
  AFPTable,
  Spinner,
  useModal,
  EmptyState,
  CounterTag,
  AFPTableRowAction,
  StatusBadge,
} from '@gsa/afp-component-library';
import BidStandardItemDetailsSubRow from './bid-standard-item-details-row';
import { BID_STANDARDITEM_TABLE_ACTIONS } from '../constants';
import AddBidLineModalWrapper from './make-and-model/index';
import RemoveConfirmationModal from './remove-confirmation-modal';
import ImportStandardItemModal from './import-standard-item/import-standard-item-modal';
import { bidLineAtom } from '../../bids/atoms/bids-atoms';
import { isFeatureEnabled } from '../../../utilities/feature-toggle';

const StandardItemsTable = ({
  tableProps,
  isDataLoading,
  refetchData,
  isBidClosed,
}) => {
  const [order] = useState('solicitationNumber ASC');
  const [selectedItem, setSelectedItem] = useState(null);
  const [bidLine] = useRecoilState(bidLineAtom);
  const isNoData = useMemo(() => !bidLine?.length, [bidLine]);
  const {
    isOpen: isRemoveOpen,
    openModal: openRemoveModal,
    closeModal: closeRemoveModal,
  } = useModal();
  const {
    isOpen: isImportOpen,
    openModal: openImportModal,
    closeModal: closeImportModal,
  } = useModal();
  const {
    isOpen: isMakeModelOpen,
    openModal: openMakeModelModal,
    closeModal: closeMakeModelModal,
  } = useModal();

  const editAction = {
    icon: 'edit',
    label: BID_STANDARDITEM_TABLE_ACTIONS.SELECT_MAKE_MODEL,
  };
  const removeAction = {
    icon: 'cancel',
    label: BID_STANDARDITEM_TABLE_ACTIONS.REMOVE,
  };
  const importAction = {
    icon: 'arrow_downward',
    label: BID_STANDARDITEM_TABLE_ACTIONS.IMPORT_DATA,
  };
  const allActions = [
    editAction,
    ...(isFeatureEnabled('bid-sin-import-data') ? [importAction] : []),
    removeAction,
  ];

  const onActionClick = (action, item) => {
    setSelectedItem(item);
    switch (action) {
      case BID_STANDARDITEM_TABLE_ACTIONS.REMOVE:
        openRemoveModal();
        break;
      case BID_STANDARDITEM_TABLE_ACTIONS.SELECT_MAKE_MODEL:
        openMakeModelModal();
        break;
      default:
        openImportModal();
    }
  };

  const onCloseRemoveModal = (toRefetch) => {
    closeRemoveModal();
    if (toRefetch === true && refetchData) refetchData();
  };

  const onCloseImportModal = (toRefetch) => {
    closeImportModal();
    if (toRefetch === true && refetchData) refetchData();
  };

  const onCloseMakeModelModal = (toRefetch) => {
    closeMakeModelModal();
    if (toRefetch === true && refetchData) refetchData();
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Standard Item',
        accessor: 'solicitationNumber',
        sortable: true,
        Cell: ({ row: { original } }) => {
          return (
            <>
              {original.standardItemNumber}-{original.title}
              {original.isSinInactive && (
                <StatusBadge
                  variant="Inactive-Gray"
                  className="margin-left-1 afp-status-tag bg-base-lightest"
                >
                  Inactive
                </StatusBadge>
              )}
            </>
          );
        },
      },
      {
        Header: 'Your total Makes and Models selected per SIN',
        accessor: 'afpMake.length',
        sortable: true,
        Cell: ({ row: { original } }) => {
          // If there is a scheduleLine there is at least one make and model
          return (
            <CounterTag
              count={(original.scheduleLine && original.afpMake?.length) || 0}
            />
          );
        },
      },
      {
        Header: 'Actions',
        sortable: false,
        headerClassName: 'cell-center',
        cellClassName: 'cell-center',
        Cell: ({ row }) => {
          if (isBidClosed) return null;
          const actionList = row.original.isSinInactive
            ? [removeAction]
            : allActions;
          return (
            <AFPTableRowAction
              row={row}
              onSelectAction={(action) => onActionClick(action, row.original)}
              actions={actionList}
            />
          );
        },
      },
    ];
  }, []);

  const renderRowSubComponent = useCallback(({ row: { original } }) => {
    return (
      <BidStandardItemDetailsSubRow
        original={original}
        openMakeModelModal={openMakeModelModal}
        setSelectedItem={setSelectedItem}
        isBidClosed={isBidClosed}
      />
    );
  }, []);

  return (
    <>
      {isDataLoading && <Spinner aria-busy="true" size="small" />}

      <AFPTable
        {...tableProps}
        data={bidLine}
        columns={columns}
        renderRowSubComponent={renderRowSubComponent}
        defaultSort={order}
      />

      {isNoData && !isDataLoading && (
        <div className="text-center margin-top-neg-2 height-full">
          <EmptyState
            hasBackground
            containerStyles="padding-y-6 height-full"
            topText={<p>No Standard Item available.</p>}
          />
        </div>
      )}

      {isRemoveOpen && selectedItem && (
        <RemoveConfirmationModal
          data={selectedItem}
          onClose={onCloseRemoveModal}
        />
      )}

      {isImportOpen && selectedItem && (
        <ImportStandardItemModal
          standardItem={selectedItem}
          onClose={onCloseImportModal}
        />
      )}

      <AddBidLineModalWrapper
        isOpen={isMakeModelOpen}
        initialData={{ siData: [selectedItem] }}
        onClose={onCloseMakeModelModal}
      />
    </>
  );
};

export default StandardItemsTable;
